import { Box, Button, CircularProgress, OutlinedInput, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import { useDebounce, usePagination } from 'ahooks'
import { DatePicker } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'
import TransactionHistoryTable from './TransactionHistoryTable'
import AssetSelect from './AssetSelect'
import NetworkSelect from './NetworkSelect'
import { FlexCol } from '@/components/common/Columns'
import { Row, RowCenter } from '@/components/common/Rows'
import { getTxHistory } from '@/api/dashboard'
import { SupportedTicker } from '@/constants/token'
import { TxHistoryChainId } from '@/api/dashboard/types'
import useMD from '@/hooks/useMD'

const TransactionHistory = () => {
  const [rawAddressInput, setAddressInput] = useState('')
  const addressInput = useDebounce(rawAddressInput, { wait: 500 })
  const [tickerName, setTickerName] = useState<SupportedTicker | ''>('')
  const [fromChainId, setFromChainId] = useState<number | ''>('')
  const [toChainId, setToChainId] = useState<number | ''>('')
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)

  const isMD = useMD()

  const { data, pagination, loading, run } = usePagination(
    async (
      { current, pageSize },
      address: string,
      tickerName: SupportedTicker | '',
      fromChainId: number | '',
      toChainId: number | '',
      fromDate: Dayjs | null,
      toDate: Dayjs | null,
    ) => {
      const resp = await getTxHistory({
        count: pageSize,
        offset: (current - 1) * pageSize,
        address: address || undefined,
        coin: tickerName || undefined,
        from_chain_id: typeof fromChainId === 'number' ? (fromChainId as TxHistoryChainId) : undefined,
        to_chain_id: typeof toChainId === 'number' ? (toChainId as TxHistoryChainId) : undefined,
        from_date: fromDate?.unix(),
        to_date: toDate?.unix(),
      })
      return { total: resp?.para?.total, list: resp?.para?.data }
    },
    {
      debounceWait: 300,
      defaultPageSize: isMD ? 10 : 6,
    },
  )

  useEffect(() => {
    run(
      {
        current: 1,
        pageSize: isMD ? 10 : 6,
      },
      addressInput,
      tickerName,
      fromChainId,
      toChainId,
      fromDate,
      toDate,
    )
  }, [addressInput, tickerName, fromChainId, toChainId, fromDate, toDate])

  return (
    <FlexCol rowGap={24}>
      <Typography variant="h1" sx={{ textShadow: '0px 0px 40px #bfff0b', fontSize: '36px', fontWeight: 400 }}>
        Transaction History
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: '1fr 1fr 1fr 1fr',
          },
          gridTemplateAreas: {
            xs: `'input' 'asset' 'from-chain' 'to-chain' 'from-date' 'to-date'`,
            sm: `'input input' 'asset asset' 'from-chain to-chain' 'from-date to-date'`,
            md: `'input input input asset' 'from-chain to-chain from-date to-date'`,
          },
          gap: 16,
          mt: 24,
        }}
      >
        <Row sx={{ gridArea: 'input' }}>
          <OutlinedInput
            placeholder="Search by Bitcoin/EVM Address"
            fullWidth
            sx={{
              height: 40,
              '::-webkit-input-placeholder': {
                color: 'cyan',
              },
            }}
            onChange={(event) => {
              setAddressInput(event.target.value)
            }}
          />
          <RowCenter sx={{ width: 40, height: 40, bgcolor: '#BFFF0B' }}>
            {loading ? <CircularProgress sx={{ color: '#000' }} size={24} /> : <SearchIcon sx={{ color: '#000' }} />}
          </RowCenter>
        </Row>

        <AssetSelect value={tickerName} onChange={setTickerName} />
        <NetworkSelect
          value={String(fromChainId)}
          onChange={(value) => {
            if (value !== '') {
              setFromChainId(Number(value))
            } else {
              setFromChainId('')
            }
          }}
          target="From"
        />
        <NetworkSelect
          value={String(toChainId)}
          onChange={(value) => {
            if (value !== '') {
              setToChainId(Number(value))
            } else {
              setToChainId('')
            }
          }}
          target="To"
        />
        <DatePicker
          value={fromDate}
          onChange={(newValue) => setFromDate(newValue)}
          slotProps={{
            textField: {
              // variant: 'outlined',
              sx: {
                '.MuiInputBase-root': {
                  height: 40,
                },
              },
            },
            field: { clearable: true },
          }}
        />
        <DatePicker
          value={toDate}
          onChange={(newValue) => setToDate(newValue)}
          slotProps={{
            textField: {
              // variant: 'outlined',
              sx: {
                '.MuiInputBase-root': {
                  height: 40,
                },
              },
            },
            field: { clearable: true },
          }}
        />
      </Box>

      <TransactionHistoryTable
        data={data?.list}
        loading={loading}
        totalPage={pagination.totalPage}
        currentPage={pagination.current}
        changeCurrent={pagination.changeCurrent}
      />
    </FlexCol>
  )
}

export default TransactionHistory
